import { Link } from "react-router-dom";
import { X, ArrowSquareOut } from "@phosphor-icons/react";
import ReviewPanel from "@/components/ReviewPanel";
import { money } from "@/lib/money";

// Row-level admin review for a sales rep's draft (proposal or packet): key terms + Approve / Send back.
export default function ReviewModal({ kind, doc, repName, onClose, onDone }) {
  const isProposal = kind === "proposal";
  const title = isProposal ? doc.company_name : `${doc.worker_name || doc.worker_email} → ${doc.project_name || doc.company_name}`;
  const rate = isProposal ? doc.hourly_rate : doc.pay_rate;
  const extras = [doc.ot_rate_applies && `OT ${money(doc.ot_rate)}/hr after ${doc.ot_threshold_hours || 40}h`,
                  doc.per_diem_applies && `per diem ${money(doc.per_diem_rate)}/day`].filter(Boolean).join(" · ");
  const lines = isProposal
    ? [["Bill rate", `${money(rate)}/hr${extras ? ` · ${extras}` : ""}`],
       ["Client contact", `${doc.contact_name} — ${doc.contact_email}${doc.contact_phone ? ` · ${doc.contact_phone}` : ""}`],
       ["Location", doc.location],
       ["Start · duration", `${doc.anticipated_start_date || "TBD"} · ${doc.anticipated_duration || "TBD"}`]]
    : [["Pay rate", `${money(rate)}/hr${extras ? ` · ${extras}` : ""}`],
       ["Worker", `${doc.worker_email}${doc.worker_id ? " · returning" : " · new contractor"}`],
       ["Project", `${doc.project_name || "—"} · ${doc.company_name} · ${doc.location}`],
       ["Start · duration", `${doc.anticipated_start_date || "TBD"} · ${doc.anticipated_duration || "TBD"}${doc.auto_assign ? " · auto-assign on return" : ""}`]];
  const detailTo = isProposal ? `/proposals/${doc.id}` : `/packets/${doc.id}`;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 overflow-y-auto" data-testid="review-modal" onClick={onClose}>
      <div className="flex items-start sm:items-center justify-center min-h-full p-4">
        <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-xl p-6 border border-slate-200 shadow-xl my-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="tinylabel text-amber-700">Review {isProposal ? "proposal" : "onboarding packet"}</div>
              <h2 className="text-xl font-black" data-testid="review-modal-title">{title}</h2>
              {repName && <div className="text-xs text-slate-500 mt-0.5">Submitted by {repName}{doc.submitted_at ? ` · ${String(doc.submitted_at).slice(0, 10)}` : ""}</div>}
            </div>
            <button type="button" onClick={onClose} data-testid="review-modal-close" className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm mb-4">
            {lines.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-xs font-bold uppercase tracking-widest text-slate-500 pt-0.5">{k}</dt>
                <dd className="text-slate-800">{v}</dd>
              </div>
            ))}
          </dl>
          {doc.description && <p className="text-sm text-slate-600 mb-4 whitespace-pre-line border-l-2 border-slate-200 pl-3">{doc.description}</p>}
          <Link to={detailTo} data-testid="review-modal-open-detail" className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-teal-900 mb-3">
            <ArrowSquareOut size={14} weight="bold" /> Open the full {isProposal ? "proposal" : "packet"}
          </Link>
          <ReviewPanel kind={kind} doc={doc} repName={repName} onDone={() => { onDone?.(); onClose(); }} />
        </div>
      </div>
    </div>
  );
}
